import { useContext, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { directoryItem } from '../../types/definitions/DirectoryItem';
import SearchService from '../../services/SearchService';
import { useOktaAuth } from '@okta/okta-react';
import LoadingContext from './search/loading-context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowUp, faCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { documentDefinition } from '../../types/Search';
import ImageService from '../../services/ImageService';
import ImageDataContext from '../../providers/imageData-context';

interface Props {
    fips: string;
    handleClose: Function;
    handleCloseWithRefresh: Function;
    state: string;
    county: string;
}

interface Paths {
    top: string;
    current: string;
    previous: string[];
}

const FolderViewModal = (props: Props) => {
    const { oktaAuth, authState } = useOktaAuth();
    const loadingCtx = useContext(LoadingContext);
    const imageDataCtx = useContext(ImageDataContext);

    const [currentData, setCurrentData] = useState<directoryItem | undefined>(undefined);
    const [searchString, setSearchString] = useState<string>('');
    const [showingSearchResults, setShowingSearchResults] = useState<boolean>(false);

    const [checkedall, setCheckedAll] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const [paths, setPaths] = useState<Paths>({ top: '', current: '', previous: [] });

    const RESTRICT_SELECTION: number = 50;
    const RESTRICT_MESSAGE = `Not all images were selected. Maximum selection is limited to ${RESTRICT_SELECTION.toString()}.`;
    const RESTRICT_MESSAGE_SHORT = `Maximum selection is limited to ${RESTRICT_SELECTION.toString()}.`;

    const clearOptions = () => {
        setShowingSearchResults(false);
        setSearchString('');
        setCheckedAll(false);
        setShowAlert(false);
        setAlertMessage('');
    };

    var uid: string = '';
    var accessToken: string = '';
    var cid: string = '';
    var groupName: string = '';
    if (authState.isAuthenticated) {
        uid = authState.accessToken.claims?.uid;
        cid = authState.accessToken.claims?.cid;
        accessToken = authState.accessToken.accessToken;
    } else {
        throw new Error('User Unauthorized');
    }

    useEffect(() => {
        // get countyId from parent and add slash
        if (props.fips !== '') {
            setPaths({
                top: `${props.fips}/`,
                current: `${props.fips}/`,
                previous: [],
            });
        } else {
            setPaths({ top: '', current: '', previous: [] });
        }
    }, [props.fips]);

    useEffect(() => {
        loadDirectory();
    }, [paths.current]);

    const loadDirectory = () => {
        if (paths.current !== '') {
            loadingCtx.setIsLoading(true, 'FolderViewModal.useEffect');
            SearchService.getDirectory(accessToken, cid, paths.current, 1, 1001)
                .then((response) => {
                    setCurrentData(response.data);
                    loadingCtx.setIsLoading(false, 'FolderViewModal.useEffect');
                })
                .catch((ex) => {
                    loadingCtx.setIsLoading(false, 'FolderViewModal.useEffect');
                });
        }
    };

    const executeSearch = () => {
        setCheckedAll(false);
        setShowAlert(false);
        setAlertMessage('');
        loadingCtx.setIsLoading(true, 'FolderViewModal.executeSearch');
        SearchService.getDirectorySearch(accessToken, cid, paths.current, searchString)
            .then((response) => {
                const castedResponse: directoryItem[] = response.data;
                if (castedResponse.length > 0) {
                    setShowingSearchResults(true);
                    setCurrentData(castedResponse[0]);
                } else {
                    setShowingSearchResults(true);
                    setCurrentData((prevState) => {
                        if (prevState === undefined) return;

                        let newState = { ...prevState };
                        newState.documents = [];
                        return newState;
                    });
                }
                loadingCtx.setIsLoading(false, 'FolderViewModal.executeSearch');
            })
            .catch((ex) => {
                loadingCtx.setIsLoading(false, 'FolderViewModal.executeSearch');
            });
    };

    const handleOk = () => {
        const pickedList = currentData?.documents.filter((item) => item.isChecked === true) ?? [];

        if (pickedList.length === 0) {
            setAlertMessage('No images were selected. Please select at least one image.');
            setShowAlert(true);
            return;
        }

        updateActivityHistory(pickedList, true).then(() => {
            props.handleCloseWithRefresh();
        });
    };

    const handleCheckAllChange = (e: any) => {
        const isChecked = e.currentTarget.checked;

        setShowAlert(false);
        setCheckedAll(isChecked);

        setCurrentData((prevState) => {
            if (prevState === undefined) {
                return;
            }

            let newState = { ...prevState };
            let newDocuments = newState.documents !== undefined ? [...newState.documents] : [];
            if (!isChecked) {
                newDocuments.forEach((d) => {
                    d.isChecked = false;
                });
            }
            if (isChecked) {
                const pickedList =
                    currentData?.documents.filter((item) => item.isChecked === true) ?? [];
                let selectedCount = pickedList.length;
                newDocuments?.forEach((d) => {
                    if (selectedCount === RESTRICT_SELECTION) {
                        setAlertMessage(RESTRICT_MESSAGE);
                        setShowAlert(true);
                        return;
                    }

                    if (!d.isChecked) {
                        d.isChecked = true;
                        selectedCount++;
                    }
                });
            }
            newState.documents = newDocuments;
            return newState;
        });
    };

    const handleCheckChange = (e: any, data: any) => {
        const isChecked = e.currentTarget.checked;
        const pickedList = currentData?.documents.filter((item) => item.isChecked === true) ?? [];
        let selectedCount = pickedList.length;

        if (isChecked && selectedCount === RESTRICT_SELECTION) {
            setCheckedAll(RESTRICT_SELECTION === selectedCount);
            return false;
        }

        setCurrentData((prevState) => {
            if (prevState === undefined) {
                return;
            }
            let newState = { ...prevState };
            let newDocuments = newState.documents !== undefined ? [...newState.documents] : [];
            newDocuments.filter((d) => d.documentName == data.documentName)[0].isChecked =
                isChecked;
            newState.documents = newDocuments;
            return newState;
        });

        e.currentTarget.checked ? selectedCount++ : selectedCount--;
        setCheckedAll(RESTRICT_SELECTION === selectedCount);

        if (selectedCount === RESTRICT_SELECTION) {
            setAlertMessage(RESTRICT_MESSAGE_SHORT);
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }

        return false;
    };

    const updateActivityHistory = async (
        fileRequest: Array<documentDefinition>,
        addToImagesSection: boolean = false
    ) => {
        // We need to add unique access dates, which are just now + 1ms each for ordering purposes
        const ordered = ImageService.addUniqueAccessDates(fileRequest);
        if (addToImagesSection) {
            // If we are adding to images section that means we clicked Ok
            imageDataCtx.addImagesToImagesList(ordered);
        } else {
            // Otherwise we are saving
            imageDataCtx.addImagesToActivityHistory(ordered);
        }
    };

    return (
        <>
            {currentData && props.fips !== '' && (
                <Modal
                    show={true}
                    size='xl'
                    scrollable={true}
                    aria-labelledby='search-modal-sizes-title-lg'
                >
                    <Modal.Header
                        closeButton
                        closeLabel='close'
                        onHide={() => {
                            clearOptions();
                            props.handleClose();
                        }}
                        bsPrefix='modal-header'
                    ></Modal.Header>
                    {(props.fips == '26099' || props.fips == '26125' || props.fips == '26163') && (
                        <div
                            style={{
                                width: '100%',
                                padding: '0.5rem 2rem',
                                backgroundColor: '#EFE4B0',
                                fontSize: '14px',
                                marginBottom: '.5rem',
                            }}
                        >
                            <b>DISCLAIMER:</b> Historical ARB Maps may{' '}
                            <u>
                                <b>ONLY</b>
                            </u>{' '}
                            be accessed to validate ARB cuts that occurred prior to November 4,
                            2010. Documents recorded on or after November 4, 2010 are posted in
                            TitlePoint to the Tax ID. Tax ID property cuts can be verified utilizing
                            Tax Assessor maps and/or County GIS websites.
                        </div>
                    )}
                    <div className='searchBarHeader d-flex justify-content-between'>
                        <div>
                            <b style={{ marginRight: '.5rem' }}>
                                Folder View -{' '}
                                {paths.current.replace(
                                    props.fips,
                                    `${props.state} ${props.county}`
                                )}
                            </b>
                            {paths.previous.length !== 0 && (
                                <button
                                    type='button'
                                    onClick={() => {
                                        setPaths((prevState) => {
                                            let newState = { ...prevState };
                                            newState.current =
                                                newState.previous[newState.previous.length - 1];
                                            let newPreviousList = [...newState.previous];
                                            newPreviousList.pop();
                                            newState.previous = newPreviousList;
                                            return newState;
                                        });
                                        clearOptions();
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowLeft}
                                        className='menuIcon'
                                        title='Back'
                                        style={{ cursor: 'pointer' }}
                                    />
                                </button>
                            )}
                            {paths.current !== paths.top && (
                                <button
                                    type='button'
                                    onClick={() => {
                                        setPaths((prevState) => {
                                            let newState: Paths = {
                                                top: prevState.top,
                                                current: prevState.top,
                                                previous: [],
                                            };
                                            return newState;
                                        });
                                        clearOptions();
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowUp}
                                        className='menuIcon'
                                        title='Top'
                                        style={{ cursor: 'pointer' }}
                                    />
                                </button>
                            )}
                        </div>
                        {currentData &&
                            currentData.documents &&
                            (currentData.documents.length > 0 || showingSearchResults) && (
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        executeSearch();
                                    }}
                                >
                                    <div style={{ whiteSpace: 'nowrap' }}>
                                        {showingSearchResults && (
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    loadDirectory();
                                                    setShowingSearchResults(false);
                                                    setSearchString('');
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCircleLeft}
                                                    className='menuIcon'
                                                    title='Clear Search Results'
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </button>
                                        )}
                                        <Form.Control
                                            type='input'
                                            className='o-form-input-name-password o-form-control okta-form-input-field input-fix'
                                            required
                                            size='sm'
                                            name='searchString'
                                            style={{
                                                display: 'inline',
                                                width: '200px',
                                                marginRight: '4px',
                                            }}
                                            value={searchString}
                                            onChange={(e) => setSearchString(e.target.value)}
                                        />
                                        <button
                                            type='submit'
                                            style={{ marginTop: '0' }}
                                            className='btn btn-primary btn-sm search-btn'
                                        >
                                            Search
                                        </button>
                                    </div>
                                </form>
                            )}
                    </div>
                    <Modal.Body>
                        <div>
                            <table id='tableSearchResults' className='table searchResults'>
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: '0',
                                            background: 'white',
                                        }}
                                    >
                                        {currentData &&
                                            currentData.children &&
                                            currentData.children.length > 0 && (
                                                <th scope='col'>
                                                    <span
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Name
                                                    </span>
                                                </th>
                                            )}
                                        {currentData &&
                                            currentData.documents &&
                                            currentData.documents.length > 0 && (
                                                <>
                                                    <th scope='col'>
                                                        <Form.Check
                                                            inline
                                                            label=''
                                                            type='checkbox'
                                                            onChange={(e) => {
                                                                handleCheckAllChange(e);
                                                            }}
                                                            //onClick={handleClick}
                                                            checked={checkedall}
                                                        />
                                                    </th>
                                                    {currentData.columnSet.map((column) => {
                                                        if (
                                                            column.toUpperCase() === 'PARENT_FOLDER'
                                                        )
                                                            return;
                                                        return (
                                                            <th scope='col' key={column}>
                                                                <span
                                                                    style={{
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {column.replaceAll('_', ' ')}
                                                                </span>
                                                            </th>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        {!(
                                            currentData &&
                                            currentData.children &&
                                            currentData.children.length > 0
                                        ) &&
                                            !(
                                                currentData &&
                                                currentData.documents &&
                                                currentData.documents.length > 0
                                            ) && <>No Records Found.</>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData &&
                                        currentData.children &&
                                        currentData.children.length > 0 &&
                                        currentData.children.map((child, i) => {
                                            return (
                                                <tr
                                                    key={i}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setPaths((prevState) => {
                                                            let newState = { ...prevState };
                                                            let newPreviousList = [
                                                                ...prevState.previous,
                                                            ];
                                                            newPreviousList.push(prevState.current);
                                                            newState.previous = newPreviousList;
                                                            newState.current = child.path;
                                                            return newState;
                                                        });
                                                    }}
                                                >
                                                    <td
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {child.name}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {currentData &&
                                        currentData.documents &&
                                        currentData.documents.length > 0 &&
                                        currentData.documents.map((doc, i) => {
                                            return (
                                                <tr key={i}>
                                                    <td scope='row'>
                                                        <Form.Check
                                                            inline
                                                            label=''
                                                            type='checkbox'
                                                            onChange={(e) => {
                                                                handleCheckChange(e, doc);
                                                            }}
                                                            checked={doc.isChecked ?? false}
                                                            id={`checkopt-${i}`}
                                                        />
                                                    </td>
                                                    {currentData.columnSet.map((column: string) => {
                                                        if (
                                                            column.toUpperCase() === 'PARENT_FOLDER'
                                                        )
                                                            return;
                                                        return (
                                                            <td key={column}>
                                                                {column.trim() != '' && (
                                                                    <span
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {
                                                                            doc.attributes.filter(
                                                                                (n) =>
                                                                                    n.name.toUpperCase() ==
                                                                                    column.toUpperCase()
                                                                            )[0]?.value
                                                                        }
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <div className='search-modal-footer'>
                            <div>
                                <span
                                    style={{
                                        color: 'red',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        visibility: showAlert == true ? 'visible' : 'hidden',
                                    }}
                                >
                                    {alertMessage}
                                </span>
                            </div>
                            <div style={{ display: 'flex' }}>
                                {currentData &&
                                    currentData.documents &&
                                    currentData.documents.length > 0 && (
                                        <>
                                            <Button variant='primary' onClick={(e) => handleOk()}>
                                                Ok
                                            </Button>
                                        </>
                                    )}
                                <Button
                                    variant='primary'
                                    onClick={(e) => {
                                        clearOptions();
                                        props.handleClose();
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default FolderViewModal;
